// Shared course start/end handling. A course stores startDate/endDate as
// exact instants (ISO strings) plus the IANA timeZone the instructor picked;
// pickers and labels show those instants in that zone.

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat'; // "z" (zone abbreviation) in formats

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

export const DEFAULT_COURSE_TIME_ZONE = 'America/Los_Angeles';

const toDate = (d) => {
    if (!d) return null;
    if (typeof d.toDate === 'function') return d.toDate(); // Firestore Timestamp or Dayjs
    const date = new Date(d);
    return Number.isNaN(date.getTime()) ? null : date;
};

export const getCourseTimeZone = (course) => course?.timeZone || DEFAULT_COURSE_TIME_ZONE;

// Convert stored course dates to Date objects. Courses saved before end times
// existed have no timeZone and store the end date as midnight, so they are
// read as Pacific Time and ending at the end of that day.
export const normalizeCourseDates = (data) => {
    const startDate = toDate(data.startDate);
    let endDate = toDate(data.endDate);
    if (!data.timeZone && endDate) {
        endDate = dayjs(endDate).tz(DEFAULT_COURSE_TIME_ZONE).endOf('day').toDate();
    }
    return { ...data, startDate, endDate, timeZone: getCourseTimeZone(data) };
};

export const hasCourseEnded = (course, now = new Date()) => {
    const end = toDate(course?.endDate);
    return !!end && end.getTime() <= now.getTime();
};

// Picker value for a stored date, shown in the course's zone.
export const toZonedDayjs = (date, timeZone) => (date ? dayjs(date).tz(timeZone) : null);

// Format a course date in the course's zone (dayjs format string).
export const formatCourseDate = (date, timeZone, format = 'YYYY-MM-DD') =>
    date ? dayjs(date).tz(timeZone).format(format) : '';

// Keep the same wall-clock time when the instructor switches zones,
// e.g. "Oct 23, 9:00 PM" stays 9:00 PM in the new zone.
export const shiftToTimeZone = (date, fromZone, toZone) => {
    if (!date) return null;
    const wallClock = dayjs(date).tz(fromZone).format('YYYY-MM-DDTHH:mm:ss');
    return dayjs.tz(wallClock, toZone).toDate();
};

const FALLBACK_TIME_ZONES = [
    'America/Los_Angeles',
    'America/Denver',
    'America/Chicago',
    'America/New_York',
    'Pacific/Honolulu',
    'America/Anchorage',
    'Europe/London',
    'Asia/Shanghai',
    'UTC',
];

// [{ value, label }] for every zone the browser knows, labelled with its current UTC offset.
export const getTimeZoneOptions = (() => {
    let cached = null;
    return () => {
        if (cached) return cached;
        const zones = typeof Intl.supportedValuesOf === 'function'
            ? Intl.supportedValuesOf('timeZone')
            : FALLBACK_TIME_ZONES;
        const now = dayjs();
        cached = [...new Set([DEFAULT_COURSE_TIME_ZONE, ...zones])]
            .map((zone) => {
                const offset = now.tz(zone).format('Z');
                return {
                    value: zone,
                    label: `(UTC${offset}) ${zone.replace(/_/g, ' ')}`,
                    offsetMinutes: now.tz(zone).utcOffset(),
                };
            })
            .sort((a, b) => a.offsetMinutes - b.offsetMinutes || a.value.localeCompare(b.value))
            .map(({ value, label }) => ({ value, label }));
        return cached;
    };
})();
